import { Flex } from '@chakra-ui/react';
import React from 'react';

import { useCodingConfig } from '../../interface';
import { PaneFooterSummary } from '../../molecules/PaneFooterSummary';
import { ReviewContent } from '../../molecules/ReviewContent';
import { ReviewTransactionsTabs } from '../../molecules/ReviewTransactionsTabs';
import { TabSelectionProvider } from '../../molecules/TabSelectionContext';
import {
  CARD_BG,
  CARD_BORDER,
  CARD_RADIUS,
  CARD_SHADOW
} from '../../styles/tokens';

/**
 * RIGHT-region organism -- composes the review/transactions tab strip above the scrollable review
 * content, above the fixed footer summary, in a bounded flex column so `ReviewContent`'s
 * `overflow-y:auto` has a definite height to scroll within (a child's `overflow-y:auto` needs a
 * bounded ancestor to take effect). Pure composition, no data logic of its own beyond seeding the
 * pane's tab selection -- each molecule independently reads its own config slice via
 * `useCodingConfig()`. Mirrors `DocumentationPane`'s exact shape (its LEFT-region counterpart,
 * including the tab-selection wrap below). Does NOT render a `<main>` (CMx already provides one); the
 * `role="region"`/`aria-label="Review and transactions"` landmark here is this organism's own
 * labelled region.
 *
 * TAB SELECTION: mirrors `DocumentationPane`'s wrap exactly -- wraps `ReviewTransactionsTabs` +
 * `ReviewContent` + `PaneFooterSummary` in `<TabSelectionProvider defaultContentKey={firstVisibleTabKey}>`,
 * `firstVisibleTabKey` being `right.tabs`' first visible tab ascending by `order` (`undefined` when
 * `right.tabs` is `[]`, the empty-shell default). `PaneFooterSummary` is inside the wrap too (matching
 * the existing `<ReviewTransactionsTabs/> <ReviewContent/> <PaneFooterSummary/>` subtree) even though
 * it doesn't itself read tab selection -- a `TabSelectionProvider` around an uninterested descendant
 * is inert, so this is simpler than carving the wrap around just the two consumers.
 *
 * CHROME (styles/tokens.ts): the root becomes the rounded/bordered/shadowed white "pane card"
 * `empty-ui.png` shows -- the same `CARD_BG`/`CARD_BORDER`/`CARD_RADIUS`/`CARD_SHADOW` treatment as
 * `DocumentationPane` (its LEFT-region counterpart), including `overflow="hidden"` to clip the
 * square-cornered tab strip and footer strip to the card's own rounded corners. The border is set via
 * a raw `style` object, not Chakra's border props -- see `DocumentationPane`'s doc comment for why
 * (jsdom does not reliably expose border sub-properties set through a stylesheet/emotion class via
 * `getComputedStyle`).
 */
export const ReviewTransactionsPane: React.FC = () => {
  const { right } = useCodingConfig();
  const firstVisibleTabKey = right.tabs
    .filter((tab) => tab.visible)
    .sort((a, b) => a.order - b.order)[0]?.contentKey;

  return (
    <Flex
      role="region"
      aria-label="Review and transactions"
      direction="column"
      h="100%"
      minH={0}
      bg={CARD_BG}
      borderRadius={CARD_RADIUS}
      boxShadow={CARD_SHADOW}
      overflow="hidden"
      style={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: CARD_BORDER
      }}
    >
      <TabSelectionProvider defaultContentKey={firstVisibleTabKey}>
        <ReviewTransactionsTabs />
        <ReviewContent />
        <PaneFooterSummary />
      </TabSelectionProvider>
    </Flex>
  );
};
